import toast from "react-hot-toast";
import { NewSubtaskFields, SubTask } from "../types";
import { showTaskLinkToast } from "./undoStack";

/** A sub-task the editor has not inserted yet. */
type DraftSubtask = { id: string; title: string } & NewSubtaskFields;

/**
 * Columns set on the draft before the first insert. An omitted column stays
 * off the insert. Carry over is always written, so an unset switch is off.
 */
function fieldsFromDraft(draft: NewSubtaskFields): NewSubtaskFields {
  return {
    persistent: draft.persistent ?? false,
    ...(draft.description !== undefined ? { description: draft.description } : {}),
    ...(draft.priority !== undefined ? { priority: draft.priority } : {}),
    ...(draft.scheduled_date !== undefined
      ? { scheduled_date: draft.scheduled_date }
      : {}),
    ...(draft.scheduled_time !== undefined
      ? { scheduled_time: draft.scheduled_time }
      : {}),
    ...(draft.deadline_date !== undefined
      ? { deadline_date: draft.deadline_date }
      : {}),
    ...(draft.duration !== undefined ? { duration: draft.duration } : {}),
    ...(draft.working_hours_only !== undefined
      ? { working_hours_only: draft.working_hours_only }
      : {}),
  };
}

/**
 * Persists every draft sub-task in `newSubtasks` under `taskId` — one insert
 * per row, mirroring the loop this replaces in `useTaskEditing.updateTask` —
 * and confirms the save with a toast.
 *
 * A single addition names it as a link into that sub-task (`"<title>"
 * added`), matching every other toast in the app that names a task or
 * sub-task. Several at once — the dialog batches every new row into one
 * save — get a single count toast rather than one per row; there's no one
 * sub-task to link to, so it's plain text.
 *
 * Returns a draft-id → real-id map for the rows that saved, same as the
 * loop it replaces, so the caller can splice real IDs back into its draft.
 */
export async function saveNewSubtasks(
  taskId: string,
  newSubtasks: DraftSubtask[],
  addSubtaskToDb: (
    taskId: string,
    title: string,
    fields?: NewSubtaskFields,
  ) => Promise<SubTask | null>,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  navigate: any,
): Promise<Map<string, string>> {
  const idMapping = new Map<string, string>();
  const addedSubtasks: SubTask[] = [];

  for (const subtask of newSubtasks) {
    const saved = await addSubtaskToDb(
      taskId,
      subtask.title,
      fieldsFromDraft(subtask),
    );
    if (saved) {
      idMapping.set(subtask.id, saved.id);
      addedSubtasks.push(saved);
    }
  }

  if (addedSubtasks.length === 1) {
    const [added] = addedSubtasks;
    showTaskLinkToast({
      label: `"${added.title}"`,
      labelSuffix: " added",
      onLabelClick: () =>
        navigate({
          to: "/task/$taskId",
          params: { taskId },
          search: (prev: Record<string, unknown>) => ({
            ...prev,
            subtask: added.id,
          }),
        }),
    });
  } else if (addedSubtasks.length > 1) {
    toast.success(`${addedSubtasks.length} subtasks added`);
  }

  return idMapping;
}
